import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Receipt, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import api from '../../api/axios';
import ReceiptModal from './ReceiptModal';
import styles from './Maintenance.module.css';

const Maintenance = () => {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedBillForReceipt, setSelectedBillForReceipt] = useState(null);

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/maintenance/my-bills');
      setBills(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch maintenance bills');
      setLoading(false);
    }
  };

  const handlePayClick = async (bill) => {
    try {
      setProcessing(true);
      toast.loading('Initializing payment...', { id: 'payment' });

      // 1. Create Order on Backend
      const { data: orderData } = await api.post('/payments/create-order', {
        maintenanceId: bill._id,
        amount: bill.remainingAmount
      });

      // 2. Open Razorpay Checkout
      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Sanskriti Shubh Aangan',
        description: `Maintenance Bill - ${bill.billingMonth}`,
        order_id: orderData.orderId,
        handler: async function (response) {
          try {
            toast.loading('Verifying payment...', { id: 'payment' });
            
            // 3. Verify Payment on Backend
            const verifyRes = await api.post('/payments/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });

            toast.success(verifyRes.data.message || 'Payment Successful!', { id: 'payment' });
            fetchBills(); // Refresh bills
          } catch (error) {
            console.error('Verification Error:', error);
            toast.error(error.response?.data?.message || 'Payment verification failed', { id: 'payment' });
          }
        },
        prefill: {
          name: '', // We could fetch from user profile context if we have it
          email: '',
          contact: ''
        },
        theme: {
          color: '#154530'
        }
      };

      const rzp = new window.Razorpay(options);
      
      rzp.on('payment.failed', function (response) {
        toast.error(response.error.description || 'Payment Failed', { id: 'payment' });
      });

      rzp.open();
      toast.dismiss('payment');
    } catch (error) {
      console.error('Payment Error:', error);
      toast.error(error.response?.data?.message || 'Failed to initialize payment', { id: 'payment' });
    } finally {
      setProcessing(false);
    }
  };

  const outstandingTotal = bills.reduce((acc, curr) => acc + curr.remainingAmount, 0);
  
  // Find last paid bill for the UI summary
  const paidBills = bills.filter(b => b.status === 'Paid' || b.status === 'Partially Paid');
  const lastPayment = paidBills.length > 0 ? paidBills[0] : null;

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Maintenance Dues" 
        breadcrumb={['Services', 'Maintenance & Payments']} 
      />

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryIcon}><AlertTriangle size={24} color="#b45309" /></div>
          <div className={styles.summaryInfo}>
            <span>Total Outstanding</span>
            <h2>₹{outstandingTotal.toLocaleString('en-IN')}</h2>
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryIcon}><Receipt size={24} color="#154530" /></div>
          <div className={styles.summaryInfo}>
            <span>Last Payment</span>
            {lastPayment ? (
              <>
                <h2>₹{lastPayment.paidAmount.toLocaleString('en-IN')}</h2>
                <small>for {lastPayment.billingMonth}</small>
              </>
            ) : (
              <h2>₹0</h2>
            )}
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>Billing History</h3>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Invoice ID</th>
                <th>Billing Month</th>
                <th>Total Amount</th>
                <th>Remaining</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="7">Loading...</td></tr> : bills.map(bill => (
                <tr key={bill._id}>
                  <td><strong>{bill.billNumber}</strong></td>
                  <td>{bill.billingMonth}</td>
                  <td>₹{bill.totalAmount}</td>
                  <td style={{ color: bill.remainingAmount > 0 ? '#b45309' : 'inherit' }}>₹{bill.remainingAmount}</td>
                  <td>{new Date(bill.dueDate).toLocaleDateString('en-GB')}</td>
                  <td>
                    <span className={`${styles.badgeStatus} ${styles[bill.status.toLowerCase().replace(' ', '')]}`}>
                      {bill.status === 'Paid' ? <CheckCircle size={12} /> : <Clock size={12} />}
                      {bill.status}
                    </span>
                  </td>
                  <td>
                    {bill.remainingAmount > 0 ? (
                      <button 
                        className={styles.payBtn} 
                        onClick={() => handlePayClick(bill)}
                        disabled={processing}
                      >
                        {processing ? 'Processing...' : 'Pay Now'}
                      </button>
                    ) : (
                      <button 
                        className={styles.downloadBtn}
                        onClick={() => setSelectedBillForReceipt(bill)}
                      >
                        Receipt
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className={styles.mobileList}>
          {loading ? <div style={{padding: '20px', textAlign: 'center'}}>Loading...</div> : bills.map(bill => (
            <div key={bill._id} className={styles.mobileCard}>
               <div className={styles.mobileCardHeader}>
                 <span className={styles.mobileMonth}>{bill.billingMonth}</span>
                 <span className={`${styles.badgeStatus} ${styles[bill.status.toLowerCase().replace(' ', '')]}`}>
                    {bill.status === 'Paid' ? <CheckCircle size={12} /> : <Clock size={12} />}
                    {bill.status}
                 </span>
               </div>
               <div className={styles.mobileCardBody}>
                 <div className={styles.mobileInfo}>
                   <small>Invoice ID</small>
                   <strong>{bill.billNumber}</strong>
                 </div>
                 <div className={styles.mobileInfo}>
                   <small>Total Amount</small>
                   <strong>₹{bill.totalAmount}</strong>
                 </div>
                 <div className={styles.mobileInfo}>
                   <small>Remaining</small>
                   <strong style={{ color: bill.remainingAmount > 0 ? '#b45309' : 'inherit' }}>₹{bill.remainingAmount}</strong>
                 </div>
                 <div className={styles.mobileInfo}>
                   <small>Due Date</small>
                   <span>{new Date(bill.dueDate).toLocaleDateString('en-GB')}</span>
                 </div>
               </div>
               <div className={styles.mobileCardFooter}>
                  {bill.remainingAmount > 0 ? (
                    <button 
                      className={styles.payBtn} 
                      onClick={() => handlePayClick(bill)} 
                      style={{width: '100%', padding: '10px', fontSize: '14px'}}
                      disabled={processing}
                    >
                      {processing ? 'Processing...' : 'Pay Now'}
                    </button>
                  ) : (
                    <button 
                      className={styles.downloadBtn} 
                      style={{width: '100%', padding: '10px', fontSize: '14px'}}
                      onClick={() => setSelectedBillForReceipt(bill)}
                    >
                      Download Receipt
                    </button>
                  )}
               </div>
            </div>
          ))}
        </div>
      </div>

      <ReceiptModal 
        bill={selectedBillForReceipt} 
        onClose={() => setSelectedBillForReceipt(null)} 
      />
    </div>
  );
};

export default Maintenance;
